import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import {
  Dropdown,
  DropdownOptionLabel,
} from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconMore } from '@sorare/core/src/atoms/icons/system/iconMore.ts';
import { iconPencil } from '@sorare/core/src/atoms/icons/system/iconPencil.ts';
import { iconTrash } from '@sorare/core/src/atoms/icons/system/iconTrash.ts';
import { iconXCircle } from '@sorare/core/src/atoms/icons/system/iconXCircle.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useToggleSubscription } from '@sorare/core/src/hooks/subscriptions/useToggleSubscription';
import { glossary } from '@sorare/core/src/lib/glossary';

import { DestroyWatchlistDialog } from '#src/components/watchlists/DestroyWatchlistDialog/index.tsx';
import { UpsertWatchlistDialog } from '#src/components/watchlists/UpsertWatchlistDialog/index.tsx';

import type { ManageWatchlist_watchlist } from './__generated__/index.graphql.ts';

const Relative = styled.div`
  isolation: isolate;
  position: relative;
`;

type Props = {
  watchlist: ManageWatchlist_watchlist;
  onRemove?: () => void;
};

export const ManageWatchlist = ({ watchlist, onRemove }: Props) => {
  const [open, setOpen] = useState(false);
  const toggleSubscription = useToggleSubscription(
    watchlist.currentUserSubscription,
    watchlist,
    () => {},
    [namedOperations.Query.WatchlistsPageQuery]
  );
  const { isMe } = useCurrentUserContext();
  const isOwner = isMe(watchlist.user);
  const isFollowing = !!watchlist.currentUserSubscription;

  const container = useRef<HTMLDivElement>(null);
  const [openEditWatchlist, setOpenEditWatchlist] = useState(false);
  const [openRemoveFromWatchlist, setOpenRemoveFromWatchlist] = useState(false);
  return (
    <Relative ref={container}>
      <Dropdown
        align="right"
        side="bottom"
        gap={4}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        // eslint-disable-next-line react-hooks/refs
        container={container.current}
        label={p => (
          <IconButton
            {...p}
            size="small"
            color="tertiary"
            onClick={() => {
              setOpen(true);
            }}
            icon={iconMore}
          />
        )}
      >
        <Vertical>
          {isOwner ? (
            <>
              <DropdownOptionLabel onClick={() => setOpenEditWatchlist(true)}>
                <FontAwesomeIcon color="var(--c-white)" icon={iconPencil} />
                <BodyM color="var(--c-white)" bold>
                  <FormattedMessage {...glossary.edit} />
                </BodyM>
              </DropdownOptionLabel>
              <DropdownOptionLabel
                onClick={() => setOpenRemoveFromWatchlist(true)}
              >
                <FontAwesomeIcon color="var(--c-red-400)" icon={iconTrash} />
                <BodyM color="var(--c-red-400)" bold>
                  <FormattedMessage {...glossary.remove} />
                </BodyM>
              </DropdownOptionLabel>
            </>
          ) : (
            <DropdownOptionLabel onClick={toggleSubscription}>
              {!!isFollowing && (
                <FontAwesomeIcon color="var(--c-red-400)" icon={iconXCircle} />
              )}
              <BodyM
                color={isFollowing ? 'var(--c-red-400)' : 'var(--c-white)'}
                bold
              >
                <FormattedMessage
                  {...(isFollowing ? glossary.unfollow : glossary.follow)}
                />
              </BodyM>
            </DropdownOptionLabel>
          )}
        </Vertical>
      </Dropdown>
      <UpsertWatchlistDialog
        open={openEditWatchlist}
        onClose={() => setOpenEditWatchlist(false)}
        watchlist={watchlist}
      />
      <DestroyWatchlistDialog
        open={openRemoveFromWatchlist}
        onClose={() => setOpenRemoveFromWatchlist(false)}
        onSuccess={onRemove}
        watchlist={watchlist}
      />
    </Relative>
  );
};

ManageWatchlist.fragments = {
  watchlist: gql`
    fragment ManageWatchlist_watchlist on Watchlist {
      slug
      user {
        slug
      }
      currentUserSubscription {
        id
        slug
      }
      ...UpsertWatchlistDialog_watchlist
      ...DestroyWatchlistDialog_watchlist
    }
    ${UpsertWatchlistDialog.fragments.watchlist}
    ${DestroyWatchlistDialog.fragments.watchlist}
  ` as TypedDocumentNode<ManageWatchlist_watchlist>,
};
